import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import {
  MeetingStateProvider,
  MeetingMethodsContext,
  useMeetingDispatch,
  useMeetingSelector,
  INITIAL_STATE
} from './meetingProviderContextDef';
import {
  ParticipantStateProvider,
  useParticipantDispatch
} from '../participant/participantStateContext';
import {
  CharacterStateProvider,
  useCharacterDispatch
} from '../character/characterStateContext';
import { eventEmitter, events } from '../utils';
import { VideoSDK } from '@videosdk.live/js-sdk';
import { version as reactSDKVersion } from '../../package.json';

/**
 *
 * @param {{
 *  children:any,
 *  config:{
 *    meetingId: string,
 *    autoConsume?: boolean,
 *    preferredProtocol?:"UDP_ONLY" | "UDP_OVER_TCP" | "TCP_ONLY",
 *    participantId?:string | undefined,
 *    name:string,
 *    micEnabled:boolean,
 *    webcamEnabled:boolean,
 *    maxResolution?:"hd" | "sd",
 *    customCameraVideoTrack?: MediaStream | undefined
 *    customMicrophoneAudioTrack?: MediaStream | undefined,
 *    signalingBaseUrl?: string | undefined;
 *    multiStream?: boolean,
 *    mode?: "SEND_AND_RECV" | "SIGNALLING_ONLY" | "RECV_ONLY",
 *    metaData?: object,
 *    defaultCamera?: 'front' | 'back',
 *    debugMode: boolean,
 *    translationLanguage?: string
 *    speakingLanguage?: string
 * },
 *  token: string,
 *  joinWithoutUserInteraction?: boolean,
 *  reinitialiseMeetingOnConfigChange?: boolean,
 *  deviceInfo?: object
 * }}
 *
 */
const StateRefSync = ({ stateRef }) => {
  const currentState = useMeetingSelector(s => s);
  stateRef.current = currentState;
  return null;
};

const MeetingProviderInner = ({
  children,
  config,
  token,
  joinWithoutUserInteraction,
  reinitialiseMeetingOnConfigChange: _reinitialiseMeetingOnConfigChange,
  deviceInfo,
  keyProvider,
  stateRef
}) => {
  const configRef_ = useRef(config);
  const tokenRef = useRef(token);
  const keyProviderRef = useRef(keyProvider);
  const deviceInfoRef = useRef(deviceInfo);
  const joinedOnFirstRender = useRef(false);
  const reinitialiseMeetingOnConfigChange = useRef(
    _reinitialiseMeetingOnConfigChange
  ).current;

  configRef_.current = config;
  tokenRef.current = token;
  keyProviderRef.current = keyProvider;
  deviceInfoRef.current = deviceInfo;

  // useMeetingDispatch from react-tracked — does NOT subscribe to state,
  // so MeetingProviderInner won't re-render on state changes.
  const dispatch = useMeetingDispatch();
  const pDispatch = useParticipantDispatch();
  const cDispatch = useCharacterDispatch();

  const prevActiveSpeakerRef = useRef(null);
  const prevMainParticipantRef = useRef(null);
  const participantListenersRef = useRef(new Map());
  const characterListenersRef = useRef(new Map());

  const _attachParticipantListeners = useCallback(
    (participant, isLocal) => {
      const pid = participant.id;

      pDispatch({
        type: 'INIT',
        id: pid,
        data: {
          participant,
          displayName: participant.displayName,
          metaData: participant.metaData,
          micOn: participant.micOn ?? false,
          webcamOn: participant.webcamOn ?? false,
          screenShareOn: participant.screenShareOn ?? false,
          screenShareAudioOn: participant.screenShareAudioOn ?? false,
          mode: participant.mode,
          isLocal: !!isLocal,
          isAgent: !!participant.isAgent,
          agentId: participant.agentId,
          agentState: participant.agentState ?? null,
          id: pid
        }
      });

      const streamKeyMap = {
        video: 'webcamStream',
        audio: 'micStream',
        share: 'screenShareStream',
        shareAudio: 'screenShareAudioStream'
      };
      const mediaKeyMap = {
        audio: 'micOn',
        video: 'webcamOn',
        share: 'screenShareOn',
        shareAudio: 'screenShareAudioOn'
      };

      participant.streams?.forEach(stream => {
        if (stream.track.readyState !== 'live') return;
        const key = streamKeyMap[stream.kind];
        const onKey = mediaKeyMap[stream.kind];
        if (key)
          pDispatch({
            type: 'UPDATE',
            id: pid,
            data: {
              [key]: stream,
              ...(onKey ? { [onKey]: true } : {})
            }
          });
      });

      const onStreamEnabled = stream => {
        if (stream.track.readyState !== 'live') return;
        const key = streamKeyMap[stream.kind];
        const onKey = mediaKeyMap[stream.kind];
        if (key)
          pDispatch({
            type: 'UPDATE',
            id: pid,
            data: {
              [key]: stream,
              ...(onKey ? { [onKey]: true } : {})
            }
          });
        eventEmitter.emit(
          `${events['participant-stream-enabled']}-${pid}`,
          stream
        );
      };
      const onStreamDisabled = stream => {
        const key = streamKeyMap[stream.kind];
        const onKey = mediaKeyMap[stream.kind];
        if (key)
          pDispatch({
            type: 'UPDATE',
            id: pid,
            data: {
              [key]: null,
              ...(onKey ? { [onKey]: false } : {})
            }
          });
        eventEmitter.emit(
          `${events['participant-stream-disabled']}-${pid}`,
          stream
        );
      };
      const onMediaStatusChanged = data => {
        const key = mediaKeyMap[data.kind];
        if (key)
          pDispatch({
            type: 'UPDATE',
            id: pid,
            data: { [key]: data.newStatus }
          });
        eventEmitter.emit(
          `${events['participant-media-status-changed']}-${pid}`,
          data
        );
      };
      const onVideoQualityChanged = data => {
        eventEmitter.emit(
          `${events['participant-video-quality-changed']}-${pid}`,
          data
        );
      };
      const onStreamPaused = data => {
        eventEmitter.emit(
          `${events['participant-stream-paused']}-${pid}`,
          data
        );
      };
      const onStreamResumed = data => {
        eventEmitter.emit(
          `${events['participant-stream-resumed']}-${pid}`,
          data
        );
      };
      const onProducerAdded = data => {
        eventEmitter.emit(
          `${events['participant-producer-added']}-${pid}`,
          data
        );
      };
      const onProducerRemoved = data => {
        eventEmitter.emit(
          `${events['participant-producer-removed']}-${pid}`,
          data
        );
      };
      const onConsumerAdded = data => {
        eventEmitter.emit(
          `${events['participant-consumer-added']}-${pid}`,
          data
        );
      };
      const onConsumerRemoved = data => {
        eventEmitter.emit(
          `${events['participant-consumer-removed']}-${pid}`,
          data
        );
      };

      const onAgentStateChanged = s => {
        pDispatch({ type: 'UPDATE', id: pid, data: { agentState: s } });
        eventEmitter.emit(`${events['agent-state-changed']}-${pid}`, s);
      };
      const onAgentTranscriptionReceived = data => {
        eventEmitter.emit(
          `${events['agent-transcription-received']}-${pid}`,
          data
        );
      };
      const onAgentMetrics = data => {
        eventEmitter.emit(`${events['agent-metrics']}-${pid}`, data);
      };

      participant.on('stream-enabled', onStreamEnabled);
      participant.on('stream-disabled', onStreamDisabled);
      participant.on('media-status-changed', onMediaStatusChanged);
      participant.on('video-quality-changed', onVideoQualityChanged);
      participant.on('stream-paused', onStreamPaused);
      participant.on('stream-resumed', onStreamResumed);
      participant.on('producer-added', onProducerAdded);
      participant.on('producer-removed', onProducerRemoved);
      participant.on('consumer-added', onConsumerAdded);
      participant.on('consumer-removed', onConsumerRemoved);
      if (participant.isAgent) {
        participant.on('agent-state-changed', onAgentStateChanged);
        participant.on(
          'agent-transcription-received',
          onAgentTranscriptionReceived
        );
        participant.on('agent-metrics', onAgentMetrics);
      }

      participantListenersRef.current.set(pid, {
        participant,
        onStreamEnabled,
        onStreamDisabled,
        onMediaStatusChanged,
        onVideoQualityChanged,
        onStreamPaused,
        onStreamResumed,
        onProducerAdded,
        onProducerRemoved,
        onConsumerAdded,
        onConsumerRemoved,
        onAgentStateChanged,
        onAgentTranscriptionReceived,
        onAgentMetrics
      });
    },
    [pDispatch]
  );

  const _detachParticipantListeners = useCallback(pid => {
    const entry = participantListenersRef.current.get(pid);
    if (!entry) return;
    const p = entry.participant;
    p.off('stream-enabled', entry.onStreamEnabled);
    p.off('stream-disabled', entry.onStreamDisabled);
    p.off('media-status-changed', entry.onMediaStatusChanged);
    p.off('video-quality-changed', entry.onVideoQualityChanged);
    p.off('stream-paused', entry.onStreamPaused);
    p.off('stream-resumed', entry.onStreamResumed);
    p.off('producer-added', entry.onProducerAdded);
    p.off('producer-removed', entry.onProducerRemoved);
    p.off('consumer-added', entry.onConsumerAdded);
    p.off('consumer-removed', entry.onConsumerRemoved);
    if (p.isAgent) {
      p.off('agent-state-changed', entry.onAgentStateChanged);
      p.off('agent-transcription-received', entry.onAgentTranscriptionReceived);
      p.off('agent-metrics', entry.onAgentMetrics);
    }
    participantListenersRef.current.delete(pid);
  }, []);

  const _attachCharacterListeners = useCallback(
    character => {
      const cid = character.id;

      const initData = {
        character,
        displayName: character.displayName,
        interactionId: character.interactionId,
        id: cid,
        characterMode: character.characterMode,
        characterRole: character.characterRole,
        characterState: character.characterState ?? null,
        knowledgeBases: character.knowledgeBases,
        language: character.language,
        metaData: character.metaData,
        micOn: character.micOn ?? false,
        webcamOn: character.webcamOn ?? false
      };
      cDispatch({ type: 'INIT', id: cid, data: initData });

      character.streams?.forEach(stream => {
        if (stream.track.readyState !== 'live') return;
        const key = {
          video: 'webcamStream',
          audio: 'micStream'
        }[stream.kind];
        if (key)
          cDispatch({ type: 'UPDATE', id: cid, data: { [key]: stream } });
      });

      const streamKeyMap = {
        video: 'webcamStream',
        audio: 'micStream'
      };
      const mediaKeyMap = {
        audio: 'micOn',
        video: 'webcamOn'
      };

      const onStreamEnabled = stream => {
        if (stream.track.readyState !== 'live') return;
        const key = streamKeyMap[stream.kind];
        if (key)
          cDispatch({ type: 'UPDATE', id: cid, data: { [key]: stream } });
        eventEmitter.emit(
          `${events['participant-stream-enabled']}-${cid}`,
          stream
        );
      };
      const onStreamDisabled = stream => {
        const key = streamKeyMap[stream.kind];
        if (key) cDispatch({ type: 'UPDATE', id: cid, data: { [key]: null } });
        eventEmitter.emit(
          `${events['participant-stream-disabled']}-${cid}`,
          stream
        );
      };
      const onMediaStatusChanged = data => {
        const key = mediaKeyMap[data.kind];
        if (key)
          cDispatch({
            type: 'UPDATE',
            id: cid,
            data: { [key]: data.newStatus }
          });
        eventEmitter.emit(
          `${events['participant-media-status-changed']}-${cid}`,
          data
        );
      };
      const onVideoQualityChanged = data => {
        eventEmitter.emit(
          `${events['participant-video-quality-changed']}-${cid}`,
          data
        );
      };

      const onCharacterStateChanged = d => {
        cDispatch({
          type: 'UPDATE',
          id: cid,
          data: { characterState: d?.status ?? d }
        });
        eventEmitter.emit(`${events['character-state-changed']}-${cid}`, d);
      };
      const onCharacterMessage = d => {
        eventEmitter.emit(`${events['character-message']}-${cid}`, d);
      };
      const onUserMessage = d => {
        eventEmitter.emit(`${events['user-message']}-${cid}`, d);
      };
      const onData = (topic, d) => {
        eventEmitter.emit(`${events['character-data']}-${cid}`, topic, d);
      };
      const onError = d => {
        eventEmitter.emit(`${events['character-error']}-${cid}`, d);
      };
      const onCharacterJoinedInstance = () => {
        eventEmitter.emit(`${events['character-joined']}-${cid}`);
      };
      const onCharacterLeftInstance = () => {
        eventEmitter.emit(`${events['character-left']}-${cid}`);
      };

      character.on('stream-enabled', onStreamEnabled);
      character.on('stream-disabled', onStreamDisabled);
      character.on('media-status-changed', onMediaStatusChanged);
      character.on('video-quality-changed', onVideoQualityChanged);
      character.on('character-state-changed', onCharacterStateChanged);
      character.on('character-message', onCharacterMessage);
      character.on('user-message', onUserMessage);
      character.on('data', onData);
      character.on('error', onError);
      character.on('character-joined', onCharacterJoinedInstance);
      character.on('character-left', onCharacterLeftInstance);

      characterListenersRef.current.set(cid, {
        character,
        onStreamEnabled,
        onStreamDisabled,
        onMediaStatusChanged,
        onVideoQualityChanged,
        onCharacterStateChanged,
        onCharacterMessage,
        onUserMessage,
        onData,
        onError,
        onCharacterJoinedInstance,
        onCharacterLeftInstance
      });
    },
    [cDispatch]
  );

  const _detachCharacterListeners = useCallback(cid => {
    const entry = characterListenersRef.current.get(cid);
    if (!entry) return;
    const c = entry.character;
    c.off('stream-enabled', entry.onStreamEnabled);
    c.off('stream-disabled', entry.onStreamDisabled);
    c.off('media-status-changed', entry.onMediaStatusChanged);
    c.off('video-quality-changed', entry.onVideoQualityChanged);
    c.off('character-state-changed', entry.onCharacterStateChanged);
    c.off('character-message', entry.onCharacterMessage);
    c.off('user-message', entry.onUserMessage);
    c.off('data', entry.onData);
    c.off('error', entry.onError);
    c.off('character-joined', entry.onCharacterJoinedInstance);
    c.off('character-left', entry.onCharacterLeftInstance);
    characterListenersRef.current.delete(cid);
  }, []);

  const _handle_participant_joined = useCallback(
    participant => {
      dispatch(prev => {
        const next = new Map(prev.participants);
        next.set(participant.id, participant);
        return { participants: next };
      });
      _attachParticipantListeners(participant, false);
      eventEmitter.emit(events['participant-joined'], participant);
    },
    [dispatch, _attachParticipantListeners]
  );

  const _handle_participant_left = useCallback(
    (participant, reason) => {
      _detachParticipantListeners(participant.id);
      pDispatch({ type: 'REMOVE', id: participant.id });
      dispatch(prev => {
        const next = new Map(prev.participants);
        next.delete(participant.id);
        return { participants: next };
      });
      eventEmitter.emit(events['participant-left'], participant, reason);
    },
    [dispatch, pDispatch, _detachParticipantListeners]
  );

  const _handle_presenter_changed = useCallback(
    presenterId => {
      dispatch(prev => ({
        presenterId,
        localScreenShareOn: presenterId === prev.meeting?.localParticipant?.id
      }));
      eventEmitter.emit(events['presenter-changed'], presenterId);
    },
    [dispatch]
  );

  const _handle_main_participant_changed = useCallback(
    participant => {
      dispatch({ mainParticipant: participant });
      const prev = prevMainParticipantRef.current;
      if (prev)
        pDispatch({
          type: 'UPDATE',
          id: prev,
          data: { isMainParticipant: false }
        });
      if (participant?.id)
        pDispatch({
          type: 'UPDATE',
          id: participant.id,
          data: { isMainParticipant: true }
        });
      prevMainParticipantRef.current = participant?.id;
      eventEmitter.emit(events['main-participant-changed'], participant);
    },
    [dispatch, pDispatch]
  );

  const _handle_speaker_changed = useCallback(
    activeSpeakerId => {
      dispatch({ activeSpeakerId });
      const prev = prevActiveSpeakerRef.current;
      if (prev) {
        pDispatch({
          type: 'UPDATE',
          id: prev,
          data: { isActiveSpeaker: false }
        });
        cDispatch({
          type: 'UPDATE',
          id: prev,
          data: { isActiveSpeaker: false }
        });
      }
      if (activeSpeakerId) {
        pDispatch({
          type: 'UPDATE',
          id: activeSpeakerId,
          data: { isActiveSpeaker: true }
        });
        cDispatch({
          type: 'UPDATE',
          id: activeSpeakerId,
          data: { isActiveSpeaker: true }
        });
      }
      prevActiveSpeakerRef.current = activeSpeakerId;
      eventEmitter.emit(events['speaker-changed'], activeSpeakerId);
    },
    [dispatch, pDispatch, cDispatch]
  );

  const _handle_chat_message = useCallback(
    data => {
      dispatch(prev => ({ messages: [...prev.messages, data] }));
      eventEmitter.emit(events['data'], data);
    },
    [dispatch]
  );

  const _handle_entry_requested = useCallback(data => {
    eventEmitter.emit(events['entry-requested'], data);
  }, []);
  const _handle_entry_responded = useCallback((id, d) => {
    eventEmitter.emit(events['entry-responded'], id, d);
  }, []);
  const _handle_video_state_changed = useCallback(data => {
    eventEmitter.emit(events['video-state-changed'], data);
  }, []);
  const _handle_video_seeked = useCallback(data => {
    eventEmitter.emit(events['video-seeked'], data);
  }, []);
  const _handle_webcam_requested = useCallback(data => {
    eventEmitter.emit(events['webcam-requested'], data);
  }, []);
  const _handle_mic_requested = useCallback(data => {
    eventEmitter.emit(events['mic-requested'], data);
  }, []);
  const _handle_switch_meeting = useCallback(d => {
    eventEmitter.emit(events['switch-meeting'], d);
  }, []);
  const _handle_error = useCallback(d => {
    eventEmitter.emit(events['error'], d);
  }, []);
  const _handle_streams_paused = useCallback(({ kind }) => {
    eventEmitter.emit(events['paused-all-streams'], { kind });
  }, []);
  const _handle_streams_resumed = useCallback(({ kind }) => {
    eventEmitter.emit(events['resumed-all-streams'], { kind });
  }, []);
  const _handle_whiteboard_started = useCallback(data => {
    eventEmitter.emit(events['whiteboard-started'], data);
  }, []);
  const _handle_whiteboard_stopped = useCallback(data => {
    eventEmitter.emit(events['whiteboard-stopped'], data);
  }, []);
  const _handle_transcription_text = useCallback(data => {
    eventEmitter.emit(events['transcription-text'], data);
  }, []);
  const _handle_translation_text = useCallback(data => {
    eventEmitter.emit(events['translation-text'], data);
  }, []);
  const _handle_translation_language_changed = useCallback(data => {
    eventEmitter.emit(events['translation-language-changed'], data);
  }, []);
  const _handle_media_relay_started = useCallback(data => {
    eventEmitter.emit(events['media-relay-started'], data);
  }, []);
  const _handle_media_relay_stopped = useCallback(data => {
    eventEmitter.emit(events['media-relay-stopped'], data);
  }, []);
  const _handle_media_relay_error = useCallback(data => {
    eventEmitter.emit(events['media-relay-error'], data);
  }, []);
  const _handle_media_relay_request_response = useCallback(data => {
    eventEmitter.emit(events['media-relay-request-response'], data);
  }, []);
  const _handle_media_relay_request_received = useCallback(data => {
    eventEmitter.emit(events['media-relay-request-received'], data);
  }, []);
  const _handle_quality_limitation_received = useCallback(data => {
    eventEmitter.emit(events['quality-limitation'], data);
  }, []);
  const _handle_audio_input_silence = useCallback(data => {
    eventEmitter.emit(events['audio-input-silence'], data);
  }, []);
  const _handle_recording_started = useCallback(() => {
    dispatch({ isRecording: true });
    eventEmitter.emit(events['recording-started']);
  }, [dispatch]);

  const _handle_recording_stopped = useCallback(() => {
    dispatch({ isRecording: false });
    eventEmitter.emit(events['recording-stopped']);
  }, [dispatch]);

  const _handle_localParticipant_stream_enabled = useCallback(
    stream => {
      if (stream.track.readyState === 'live') {
        if (stream.kind === 'video') dispatch({ localWebcamOn: true });
        else if (stream.kind === 'audio') dispatch({ localMicOn: true });
      }
    },
    [dispatch]
  );

  const _handle_localParticipant_stream_disabled = useCallback(
    stream => {
      if (stream.kind === 'video') dispatch({ localWebcamOn: false });
      else if (stream.kind === 'audio') dispatch({ localMicOn: false });
    },
    [dispatch]
  );

  const _handle_live_stream_started = useCallback(
    data => {
      dispatch({ isLiveStreaming: true });
      eventEmitter.emit(events['live-stream-started'], data);
    },
    [dispatch]
  );

  const _handle_live_stream_stopped = useCallback(() => {
    dispatch({ isLiveStreaming: false });
    eventEmitter.emit(events['live-stream-stopped']);
  }, [dispatch]);

  const _handle_hls_started = useCallback(
    data => {
      dispatch({ isHls: true });
      eventEmitter.emit(events['hls-started'], data);
    },
    [dispatch]
  );

  const _handle_hls_stopped = useCallback(() => {
    dispatch({ isHls: false });
    eventEmitter.emit(events['hls-stopped']);
  }, [dispatch]);

  const _handle_meeting_joined = useCallback(
    data => {
      if (data.switchRoomId) {
        dispatch(prev => ({
          messages: [...prev.messages, ...data.messages]
        }));
      } else {
        joinedOnFirstRender.current = true;
        dispatch(prev => ({
          messages: [...prev.messages, ...data.messages],
          isMeetingJoined: true
        }));
      }
      eventEmitter.emit(events['meeting-joined'], data);
    },
    [dispatch]
  );

  const _handle_pin_state_changed = useCallback(
    ({ participantId, state, pinnedBy }) => {
      dispatch(prev => {
        const next = new Map(prev.pinnedParticipants);
        if (!state.cam && !state.share) next.delete(participantId);
        else next.set(participantId, state);
        return { pinnedParticipants: next };
      });
      pDispatch({
        type: 'UPDATE',
        id: participantId,
        data: {
          pinState:
            !state.cam && !state.share ? { cam: false, share: false } : state
        }
      });
      eventEmitter.emit(events['pin-state-changed'], {
        participantId,
        state,
        pinnedBy
      });
    },
    [dispatch, pDispatch]
  );

  const _handle_connection_open = useCallback(
    connection => {
      dispatch(prev => {
        const next = new Map(prev.connections);
        next.set(connection.id, connection);
        return { connections: next };
      });
      eventEmitter.emit(events['connection-open'], connection);
    },
    [dispatch]
  );

  const _handle_connection_close = useCallback(
    connectionId => {
      dispatch(prev => {
        const next = new Map(prev.connections);
        next.delete(connectionId);
        return { connections: next };
      });
      eventEmitter.emit(events['connection-close'], connectionId);
    },
    [dispatch]
  );

  const _handle_recording_state_changed = useCallback(
    data => {
      dispatch({ recordingState: data?.status });
      eventEmitter.emit(events['recording-state-changed'], data);
    },
    [dispatch]
  );

  const _handle_livestream_state_changed = useCallback(
    data => {
      dispatch({ livestreamState: data?.status });
      eventEmitter.emit(events['livestream-state-changed'], data);
    },
    [dispatch]
  );

  const _handle_hls_state_changed = useCallback(
    data => {
      const patch = { hlsState: data?.status };
      if (data?.status === 'HLS_PLAYABLE') {
        patch.hlsUrls = {
          downstreamUrl: data.downstreamUrl,
          playbackHlsUrl: data.playbackHlsUrl,
          livestreamUrl: data.livestreamUrl
        };
      } else if (data?.status === 'HLS_STOPPED') {
        patch.hlsUrls = {
          downstreamUrl: null,
          playbackHlsUrl: null,
          livestreamUrl: null
        };
      }
      dispatch(patch);
      eventEmitter.emit(events['hls-state-changed'], data);
    },
    [dispatch]
  );

  const _handle_meeting_state_changed = useCallback(
    data => {
      if (data?.state === 'RECONNECTING') dispatch({ isMeetingJoined: false });
      eventEmitter.emit(events['meeting-state-changed'], data);
    },
    [dispatch]
  );

  const _handle_socket_state_changed = useCallback(
    data => {
      if (data?.state === 'SOCKET_DISCONNECTED')
        dispatch({ isMeetingJoined: false });
    },
    [dispatch]
  );

  const _handle_transcription_state_changed = useCallback(
    data => {
      dispatch({ transcriptionState: data?.status });
      eventEmitter.emit(events['transcription-state-changed'], data);
    },
    [dispatch]
  );

  const _handle_translation_state_changed = useCallback(
    data => {
      dispatch({ translationState: data?.status });
      eventEmitter.emit(events['translation-state-changed'], data);
    },
    [dispatch]
  );

  const _handle_participant_mode_changed = useCallback(
    data => {
      dispatch(prev => {
        const next = new Map(prev.participants);
        const part = next.get(data.participantId);
        if (!part) return {};
        next.set(part.id, { ...part, mode: data.mode });
        const patch = { participants: next };
        if (data.participantId === prev.meeting?.localParticipant?.id) {
          patch.localParticipant = {
            ...prev.localParticipant,
            mode: data.mode
          };
        }
        return patch;
      });
      pDispatch({
        type: 'UPDATE',
        id: data.participantId,
        data: { mode: data.mode }
      });
      eventEmitter.emit(events['participant-mode-changed'], data);
    },
    [dispatch, pDispatch]
  );

  const _handle_character_joined = useCallback(
    character => {
      dispatch(prev => {
        const next = new Map(prev.characters);
        next.set(character.id, character);
        return { characters: next };
      });
      _attachCharacterListeners(character);
      eventEmitter.emit(events['character-joined'], character);
    },
    [dispatch, _attachCharacterListeners]
  );

  const _handle_character_left = useCallback(
    character => {
      _detachCharacterListeners(character.id);
      cDispatch({ type: 'REMOVE', id: character.id });
      dispatch(prev => {
        const next = new Map(prev.characters);
        next.delete(character.id);
        return { characters: next };
      });
      eventEmitter.emit(events['character-left'], character);
    },
    [dispatch, cDispatch, _detachCharacterListeners]
  );

  // Store all handlers in a ref so methods.join() can read them
  // without being recreated when handlers change.
  const handlersRef = useRef({});
  handlersRef.current = {
    _handle_meeting_state_changed,
    _handle_socket_state_changed,
    _handle_participant_joined,
    _handle_participant_left,
    _handle_presenter_changed,
    _handle_main_participant_changed,
    _handle_speaker_changed,
    _handle_entry_requested,
    _handle_entry_responded,
    _handle_chat_message,
    _handle_recording_started,
    _handle_recording_stopped,
    _handle_meeting_joined,
    _handle_live_stream_started,
    _handle_live_stream_stopped,
    _handle_video_state_changed,
    _handle_video_seeked,
    _handle_webcam_requested,
    _handle_mic_requested,
    _handle_pin_state_changed,
    _handle_streams_paused,
    _handle_streams_resumed,
    _handle_connection_open,
    _handle_connection_close,
    _handle_switch_meeting,
    _handle_error,
    _handle_hls_started,
    _handle_hls_stopped,
    _handle_recording_state_changed,
    _handle_livestream_state_changed,
    _handle_hls_state_changed,
    _handle_participant_mode_changed,
    _handle_whiteboard_started,
    _handle_whiteboard_stopped,
    _handle_transcription_state_changed,
    _handle_transcription_text,
    _handle_translation_state_changed,
    _handle_translation_text,
    _handle_translation_language_changed,
    _handle_character_joined,
    _handle_character_left,
    _handle_media_relay_started,
    _handle_media_relay_stopped,
    _handle_media_relay_error,
    _handle_media_relay_request_response,
    _handle_media_relay_request_received,
    _handle_quality_limitation_received,
    _handle_audio_input_silence,
    _handle_localParticipant_stream_enabled,
    _handle_localParticipant_stream_disabled,
    _attachParticipantListeners,
    _attachCharacterListeners
  };

  // _handle_meeting_left needs handlersRef to detach all listeners
  const _handle_meeting_left = useCallback(
    data => {
      eventEmitter.emit(events['meeting-left'], data);
      const meeting = stateRef.current.meeting;
      const h = handlersRef.current;
      if (meeting && typeof meeting.off === 'function') {
        meeting.off('meeting-state-changed', h._handle_meeting_state_changed);
        meeting.off('socket-state-changed', h._handle_socket_state_changed);
        meeting.off('participant-joined', h._handle_participant_joined);
        meeting.off('participant-left', h._handle_participant_left);
        meeting.off('presenter-changed', h._handle_presenter_changed);
        meeting.off(
          'main-participant-changed',
          h._handle_main_participant_changed
        );
        meeting.off('speaker-changed', h._handle_speaker_changed);
        meeting.off('entry-requested', h._handle_entry_requested);
        meeting.off('entry-responded', h._handle_entry_responded);
        meeting.off('data', h._handle_chat_message);
        meeting.off('recording-started', h._handle_recording_started);
        meeting.off('recording-stopped', h._handle_recording_stopped);
        meeting.off('meeting-joined', h._handle_meeting_joined);
        meeting.off('meeting-left', h._handle_meeting_left);
        meeting.off('livestream-started', h._handle_live_stream_started);
        meeting.off('livestream-stopped', h._handle_live_stream_stopped);
        meeting.off('video-state-changed', h._handle_video_state_changed);
        meeting.off('video-seeked', h._handle_video_seeked);
        meeting.off('webcam-requested', h._handle_webcam_requested);
        meeting.off('mic-requested', h._handle_mic_requested);
        meeting.off('pin-state-changed', h._handle_pin_state_changed);
        meeting.off('paused-all-streams', h._handle_streams_paused);
        meeting.off('resumed-all-streams', h._handle_streams_resumed);
        meeting.off('connection-open', h._handle_connection_open);
        meeting.off('connection-close', h._handle_connection_close);
        meeting.off('switch-meeting', h._handle_switch_meeting);
        meeting.off('error', h._handle_error);
        meeting.off('hls-started', h._handle_hls_started);
        meeting.off('hls-stopped', h._handle_hls_stopped);
        meeting.off(
          'recording-state-changed',
          h._handle_recording_state_changed
        );
        meeting.off(
          'livestream-state-changed',
          h._handle_livestream_state_changed
        );
        meeting.off('hls-state-changed', h._handle_hls_state_changed);
        meeting.off(
          'participant-mode-changed',
          h._handle_participant_mode_changed
        );
        meeting.off('whiteboard-stopped', h._handle_whiteboard_stopped);
        meeting.off('whiteboard-started', h._handle_whiteboard_started);
        meeting.off(
          'transcription-state-changed',
          h._handle_transcription_state_changed
        );
        meeting.off('transcription-text', h._handle_transcription_text);
        meeting.off(
          'translation-state-changed',
          h._handle_translation_state_changed
        );
        meeting.off('translation-text', h._handle_translation_text);
        meeting.off(
          'translation-language-changed',
          h._handle_translation_language_changed
        );
        meeting.off('character-joined', h._handle_character_joined);
        meeting.off('character-left', h._handle_character_left);
        meeting.off('media-relay-started', h._handle_media_relay_started);
        meeting.off('media-relay-stopped', h._handle_media_relay_stopped);
        meeting.off('media-relay-error', h._handle_media_relay_error);
        meeting.off(
          'media-relay-request-response',
          h._handle_media_relay_request_response
        );
        meeting.off(
          'media-relay-request-received',
          h._handle_media_relay_request_received
        );
        meeting.off(
          'quality-limitation',
          h._handle_quality_limitation_received
        );
        meeting.off('audio-input-silence', h._handle_audio_input_silence);
        meeting.localParticipant?.off(
          'stream-enabled',
          h._handle_localParticipant_stream_enabled
        );
        meeting.localParticipant?.off(
          'stream-disabled',
          h._handle_localParticipant_stream_disabled
        );
      }
      // Clean up all per-participant listeners (snapshot keys to avoid mutation during iteration)
      for (const pid of [...participantListenersRef.current.keys()]) {
        _detachParticipantListeners(pid);
      }
      // Clean up all per-character listeners
      for (const cid of [...characterListenersRef.current.keys()]) {
        _detachCharacterListeners(cid);
      }
      pDispatch('RESET');
      cDispatch('RESET');
      dispatch('RESET');
    },
    [
      dispatch,
      pDispatch,
      cDispatch,
      _detachParticipantListeners,
      _detachCharacterListeners
    ]
  );

  // Add _handle_meeting_left to handlersRef after it's defined
  handlersRef.current._handle_meeting_left = _handle_meeting_left;

  // methods is stable — only depends on dispatch (which is stable from useReducer)
  const methods = useMemo(
    () => ({
      join: () => {
        const m = VideoSDK.initMeeting(configRef_.current);
        const { localParticipant, participants, characters } = m;
        participants.set(localParticipant.id, localParticipant);
        const initPatch = {
          meetingId: m.id,
          meeting: m,
          baseUrl: m.baseUrl,
          localParticipant,
          participants,
          characters,
          selectedCameraDevice: m.selectedCameraDevice,
          selectedMicrophoneDevice: m.selectedMicrophoneDevice
        };
        dispatch(initPatch);

        // Patch stateRef in-place — events fire before React renders, callbacks need fresh state.
        Object.assign(stateRef.current, initPatch);

        const h = handlersRef.current;
        m.on('meeting-state-changed', h._handle_meeting_state_changed);
        m.on('socket-state-changed', h._handle_socket_state_changed);
        m.on('participant-joined', h._handle_participant_joined);
        m.on('participant-left', h._handle_participant_left);
        m.on('presenter-changed', h._handle_presenter_changed);
        m.on('main-participant-changed', h._handle_main_participant_changed);
        m.on('speaker-changed', h._handle_speaker_changed);
        m.on('entry-requested', h._handle_entry_requested);
        m.on('entry-responded', h._handle_entry_responded);
        m.on('data', h._handle_chat_message);
        m.on('recording-started', h._handle_recording_started);
        m.on('recording-stopped', h._handle_recording_stopped);
        m.on('meeting-joined', h._handle_meeting_joined);
        m.on('meeting-left', h._handle_meeting_left);
        m.on('livestream-started', h._handle_live_stream_started);
        m.on('livestream-stopped', h._handle_live_stream_stopped);
        m.on('video-state-changed', h._handle_video_state_changed);
        m.on('video-seeked', h._handle_video_seeked);
        m.on('webcam-requested', h._handle_webcam_requested);
        m.on('mic-requested', h._handle_mic_requested);
        m.on('pin-state-changed', h._handle_pin_state_changed);
        m.on('paused-all-streams', h._handle_streams_paused);
        m.on('resumed-all-streams', h._handle_streams_resumed);
        m.on('connection-open', h._handle_connection_open);
        m.on('connection-close', h._handle_connection_close);
        m.on('switch-meeting', h._handle_switch_meeting);
        m.on('error', h._handle_error);
        m.on('hls-started', h._handle_hls_started);
        m.on('hls-stopped', h._handle_hls_stopped);
        m.on('recording-state-changed', h._handle_recording_state_changed);
        m.on('livestream-state-changed', h._handle_livestream_state_changed);
        m.on('hls-state-changed', h._handle_hls_state_changed);
        m.on('participant-mode-changed', h._handle_participant_mode_changed);
        m.on(
          'transcription-state-changed',
          h._handle_transcription_state_changed
        );
        m.on('transcription-text', h._handle_transcription_text);
        m.on('translation-state-changed', h._handle_translation_state_changed);
        m.on('translation-text', h._handle_translation_text);
        m.on(
          'translation-language-changed',
          h._handle_translation_language_changed
        );
        m.on('whiteboard-started', h._handle_whiteboard_started);
        m.on('whiteboard-stopped', h._handle_whiteboard_stopped);
        m.on('character-joined', h._handle_character_joined);
        m.on('character-left', h._handle_character_left);
        m.on('media-relay-started', h._handle_media_relay_started);
        m.on('media-relay-stopped', h._handle_media_relay_stopped);
        m.on('media-relay-error', h._handle_media_relay_error);
        m.on(
          'media-relay-request-response',
          h._handle_media_relay_request_response
        );
        m.on(
          'media-relay-request-received',
          h._handle_media_relay_request_received
        );
        m.on('quality-limitation', h._handle_quality_limitation_received);
        m.on('audio-input-silence', h._handle_audio_input_silence);
        m.localParticipant.on(
          'stream-enabled',
          h._handle_localParticipant_stream_enabled
        );
        m.localParticipant.on(
          'stream-disabled',
          h._handle_localParticipant_stream_disabled
        );
        h._attachParticipantListeners(m.localParticipant, true);
        m.join();
      },

      leave: () => {
        stateRef.current.meeting?.leave();
      },
      end: () => {
        stateRef.current.meeting?.end();
      },
      muteMic: () => {
        stateRef.current.meeting?.muteMic();
      },
      unmuteMic: t => {
        stateRef.current.meeting?.unmuteMic(t);
      },
      toggleMic: t => {
        const { localMicOn, meeting } = stateRef.current;
        localMicOn ? meeting?.muteMic() : meeting?.unmuteMic(t);
      },
      disableWebcam: () => {
        stateRef.current.meeting?.disableWebcam();
      },
      enableWebcam: t => {
        stateRef.current.meeting?.enableWebcam(t);
      },
      toggleWebcam: t => {
        const { localWebcamOn, meeting } = stateRef.current;
        localWebcamOn ? meeting?.disableWebcam() : meeting?.enableWebcam(t);
      },
      disableScreenShare: () => {
        stateRef.current.meeting?.disableScreenShare();
      },
      enableScreenShare: t => {
        stateRef.current.meeting?.enableScreenShare(t);
      },
      toggleScreenShare: t => {
        const { localScreenShareOn, meeting } = stateRef.current;
        localScreenShareOn
          ? meeting?.disableScreenShare()
          : meeting?.enableScreenShare(t);
      },
      pauseAllStreams: k => {
        stateRef.current.meeting?.pauseAllStreams(k);
      },
      resumeAllStreams: k => {
        stateRef.current.meeting?.resumeAllStreams(k);
      },
      startRecording: (w, a, c, t) => {
        stateRef.current.meeting?.startRecording(w, a, c, t);
      },
      stopRecording: () => {
        const { meeting, isRecording } = stateRef.current;
        if (meeting && isRecording) meeting.stopRecording();
      },
      changeWebcam: obj => {
        const m = stateRef.current.meeting;
        m?.changeWebcam(obj);
        dispatch({ selectedCameraDevice: m?.selectedCameraDevice });
      },
      changeMic: obj => {
        const m = stateRef.current.meeting;
        m?.changeMic(obj);
        dispatch({ selectedMicrophoneDevice: m?.selectedMicrophoneDevice });
      },
      send: async (p, o) => stateRef.current.meeting?.send(p, o),
      respondEntry: (id, d) => {
        stateRef.current.meeting?.respondEntry(id, d);
      },
      getMics: async () => stateRef.current.meeting?.getMics(),
      getWebcams: async () => stateRef.current.meeting?.getWebcams(),
      replaceWebcamStream: s => {
        stateRef.current.meeting?.replaceWebcamStream(s);
      },
      switchTo: ({ meetingId, token }) => {
        stateRef.current.meeting?.switchTo({ meetingId, token });
      },
      changeMode: mode => {
        stateRef.current.meeting?.changeMode(mode);
      },
      startVideo: ({ link }) => {
        stateRef.current.meeting?.startVideo({ link });
      },
      stopVideo: () => {
        stateRef.current.meeting?.stopVideo();
      },
      resumeVideo: () => {
        stateRef.current.meeting?.resumeVideo();
      },
      pauseVideo: ({ currentTime }) => {
        stateRef.current.meeting?.pauseVideo({ currentTime });
      },
      seekVideo: ({ currentTime }) => {
        stateRef.current.meeting?.seekVideo({ currentTime });
      },
      startLivestream: (i, c) => {
        stateRef.current.meeting?.startLivestream(i, c);
      },
      stopLivestream: () => {
        stateRef.current.meeting?.stopLivestream();
      },
      connectTo: async ({ meetingId, payload }) => {
        await stateRef.current.meeting?.connectTo({ meetingId, payload });
      },
      startHls: (c, t) => {
        stateRef.current.meeting?.startHls(c, t);
      },
      stopHls: () => {
        stateRef.current.meeting?.stopHls();
      },
      enableAdaptiveSubscription: () => {
        stateRef.current.meeting?.enableAdaptiveSubscription();
      },
      disableAdaptiveSubscription: () => {
        stateRef.current.meeting?.disableAdaptiveSubscription();
      },
      requestMediaRelay: o => {
        stateRef.current.meeting?.requestMediaRelay(o);
      },
      stopMediaRelay: id => {
        stateRef.current.meeting?.stopMediaRelay(id);
      }
    }),
    [dispatch]
  );

  const initSDK = useCallback(() => {
    VideoSDK.config(tokenRef.current);
    keyProviderRef.current && VideoSDK.setKeyProvider(keyProviderRef.current);
    VideoSDK.analytics(
      deviceInfoRef.current || {
        sdkType: 'react-web',
        sdkVersion: reactSDKVersion
      }
    );
    if (joinWithoutUserInteraction) methods.join();
  }, [methods, joinWithoutUserInteraction]);

  useEffect(() => {
    initSDK();
    return () => {
      for (const pid of [...participantListenersRef.current.keys()]) {
        _detachParticipantListeners(pid);
      }
      for (const cid of [...characterListenersRef.current.keys()]) {
        _detachCharacterListeners(cid);
      }
    };
  }, []);

  useEffect(() => {
    if (reinitialiseMeetingOnConfigChange && joinedOnFirstRender.current) {
      methods.leave();
      initSDK();
      if (!joinWithoutUserInteraction) methods.join();
    }
  }, [config.meetingId, token, reinitialiseMeetingOnConfigChange]);

  return (
    <MeetingMethodsContext.Provider value={methods}>
      {children}
    </MeetingMethodsContext.Provider>
  );
};

const MeetingProvider = props => {
  const localStateRef = useRef({ ...INITIAL_STATE });
  return (
    <MeetingStateProvider>
      <ParticipantStateProvider>
        <CharacterStateProvider>
          <StateRefSync stateRef={localStateRef} />
          <MeetingProviderInner {...props} stateRef={localStateRef} />
        </CharacterStateProvider>
      </ParticipantStateProvider>
    </MeetingStateProvider>
  );
};

export default MeetingProvider;
